import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { useServerFn } from '@tanstack/react-start';
import { BarChart2, Check, Monitor, Moon, Search, Sun } from 'lucide-react';
import type React from 'react';
import { forwardRef, useDeferredValue, useEffect, useRef, useState } from 'react';
import { VisuallyHidden } from 'react-aria';

import { ButtonClose } from '~/components/buttons/button-close.tsx';
import countViewedNotifications from '~/components/main-navbar/notifications/count-viewed-notifications.ts';
import { getNavbarNotificationsQueryOptions } from '~/components/main-navbar/notifications/query.ts';
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
	CommandSeparator,
	CommandShortcut,
	CustomCommandGroup,
	CustomCommandItem,
} from '~/components/shadcn/command.tsx';
import { DialogTitle } from '~/components/shadcn/dialog.tsx';
import { ScrollArea } from '~/components/shadcn/scroll-area.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import type { InstrumentSearchQuoteResponseFiltered } from '~/integrations/yahoo/queries.d.ts';
import { useKeyboardShortcut } from '~/modules/keyboard/hooks.ts';
import { addNavbarUserSearch } from '~/server/app/add-navbar-user-search.ts';
import type { AppShellData } from '~/server/app/get-app-shell-data.ts';
import { searchNavbarInstruments } from '~/server/app/search-navbar-instruments.ts';
import { cn } from '~/utils/cn.ts';
import { formatNumber } from '~/utils/format.ts';
import { useOpenChangeHandlers } from '~/utils/hooks/useOpenChangeHandlers.ts';
import { saveThemePreference, Theme, useRequestInfo } from '~/utils/theme-utils.ts';

import { CommandBadge } from './navigation-command-palette/command-badge.tsx';
import { navigationOptions } from './navigation-command-palette/options.tsx';

interface NavigationCommandPaletteProps {
	readonly command?: string[];
	readonly isDialogOpen: boolean;
	readonly isMobile?: boolean;
	readonly isOpen: boolean;
	readonly latestSearches: AppShellData['latestSearches'];
	readonly searchSuggestions: AppShellData['searchSuggestions'];
	readonly setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	readonly showKeyboardNavigation?: boolean;
	readonly triggerClassName?: string;
}

function extractInstrumentTicker(to: string): string | null {
	if (!to.startsWith('/instrument/')) {
		return null;
	}

	return to.split('/').at(-1) ?? null;
}

export const NavigationCommandPalette: React.FunctionComponent<NavigationCommandPaletteProps> = ({
	command,
	isMobile = false,
	isOpen,
	latestSearches,
	searchSuggestions,
	setIsOpen,
	showKeyboardNavigation,
	triggerClassName,
}) => {
	const t = useTranslations();
	const addUserSearch = useServerFn(addNavbarUserSearch);
	const searchInstruments = useServerFn(searchNavbarInstruments);
	const router = useRouter();
	const requestInfo = useRequestInfo();
	const preferredTheme = requestInfo.userPrefs.theme;
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const previousPathnameRef = useRef(pathname);
	const [searchValue, setSearchValue] = useState('');
	const [latestSearchState, setLatestSearchState] = useState(latestSearches);
	const deferredSearchValue = useDeferredValue(searchValue);
	const trimmedSearchValue = searchValue.trim();
	const trimmedDeferredSearchValue = deferredSearchValue.trim();
	const searchResultsQuery = useQuery({
		enabled: isOpen && trimmedDeferredSearchValue.length > 0,
		queryFn: () =>
			searchInstruments({
				data: {
					query: trimmedDeferredSearchValue,
				},
			}),
		queryKey: ['navbar-instrument-search', trimmedDeferredSearchValue],
	});
	const notificationsQuery = useQuery(getNavbarNotificationsQueryOptions());
	const unreadCount = countViewedNotifications(notificationsQuery.data);
	const unreadCountLabel = unreadCount > 0 ? formatNumber(unreadCount, requestInfo.hints.locale) : undefined;
	const searchResults = (searchResultsQuery.data ?? []) as InstrumentSearchQuoteResponseFiltered;
	const isLoading = searchResultsQuery.isFetching;

	const { handleDelayedOpenChange, handleImmediateOpenChange, showTriggerText } = useOpenChangeHandlers({
		isOpen,
		setIsOpen,
	});

	useKeyboardShortcut({
		callback: handleImmediateOpenChange,
		command,
		shouldFire: !isOpen,
	});

	useEffect(() => {
		if (!isOpen) {
			setSearchValue('');
		}
	}, [isOpen]);

	useEffect(() => {
		setLatestSearchState(latestSearches);
	}, [latestSearches]);

	useEffect(() => {
		const previousPathname = previousPathnameRef.current;
		previousPathnameRef.current = pathname;

		if (!isOpen || previousPathname === pathname) {
			return;
		}

		const ticker = extractInstrumentTicker(pathname);
		if (ticker) {
			setLatestSearchState((previousState) => {
				const nextItems = [
					{
						instrument_id: -1,
						ticker,
					},
					...(previousState ?? []).filter((item) => item.ticker !== ticker),
				];

				return nextItems.slice(0, 3);
			});

			void addUserSearch({
				data: {
					ticker,
				},
			});
		}

		const timeout = setTimeout(() => {
			setIsOpen(false);
		}, 300);

		return () => {
			clearTimeout(timeout);
		};
	}, [pathname, isOpen, setIsOpen, addUserSearch]);

	const hasSearchResults = searchResults.length > 0;
	const defaultValue = searchResults.at(0)?.symbol;

	async function handleSelectTheme(themeValue: Theme): Promise<void> {
		setIsOpen(false);
		await saveThemePreference(themeValue);
		await router.invalidate();
	}

	return (
		<>
			<CommandTrigger
				command={command}
				handleOpenChange={handleImmediateOpenChange}
				isMobile={isMobile}
				isOpen={isOpen}
				showKeyboardNavigation={showKeyboardNavigation}
				showTriggerText={!isMobile && showTriggerText}
				triggerClassName={triggerClassName}
			/>

			<CommandDialog
				classNames={{
					command: 'rounded-none h-screen border-x border-border',
					dialogContent: cn(
						'z-[calc(var(--z-index-navbar)-1)]',
						'rounded-none rounded-b-none border-none bg-card',
						'shadow-none sm:rounded-none sm:rounded-b-none sm:shadow-none',
					),
					overlay: 'z-[calc(var(--z-index-navbar)-1)] backdrop-blur-none duration-500',
					portal: cn(
						'absolute left-0 z-[calc(var(--z-index-navbar)-1)] justify-start pt-0 align-start duration-500 sm:left-sidebar-width sm:pt-0',
					),
				}}
				defaultValue={defaultValue}
				onOpenChange={() => handleDelayedOpenChange()}
				onPointerDownOutside={handleDelayedOpenChange}
				open={isOpen}
				shouldFilter={false}
				shouldSort={true}
				transition={isMobile ? 'fromBottom' : 'fromLeft'}
			>
				<VisuallyHidden>
					<DialogTitle>{t('nav.search')}</DialogTitle>
				</VisuallyHidden>
				<CommandInput
					CustomComponent={<ButtonClose onClose={handleDelayedOpenChange} />}
					containerClassName="py-2"
					loading={isLoading}
					maxLength={30}
					onValueChange={(value) => {
						const cleanValue = value.replace(/[^\w\s.-]/gi, '');
						setSearchValue(cleanValue);
					}}
					placeholder={t('nav.searchPlaceholder')}
					value={searchValue}
				/>
				<ScrollArea classNames={{ root: 'max-h-[calc(100dvh-64px)]' }}>
					<CommandList className="sidebar max-h-fit overflow-y-auto">
						{trimmedSearchValue.length > 0 ? (
							<CommandSearchResults data={searchResults} hasResults={hasSearchResults} isIdle={!isLoading} />
						) : null}

						{latestSearchState?.length ? (
							<>
								<CommandSeparator alwaysRender />
								<CommandLatestSearches lastSearches={latestSearchState} />
							</>
						) : null}

						{searchSuggestions?.length ? (
							<>
								<CommandSeparator alwaysRender />
								<CommandSearchSuggestions searchSuggestions={searchSuggestions} />
							</>
						) : null}

						<CommandSeparator alwaysRender />
						<CustomCommandGroup heading={t('nav.navigation')}>
							{navigationOptions.map((option) => (
								<CommandNavigationOption
									command={option.command}
									href={option.href}
									Icon={option.Icon}
									key={option.nameId}
									name={t(option.nameId)}
									unreadCountLabel={option.href === '/notifications' ? unreadCountLabel : undefined}
								/>
							))}
						</CustomCommandGroup>
						{isMobile ? (
							<>
								<CommandSeparator alwaysRender />
								<CustomCommandGroup heading={t('nav.appearance')}>
									<CustomCommandItem onSelect={() => void handleSelectTheme(Theme.LIGHT)} value="Theme: Light">
										<Sun className="mr-2 size-4" />
										<span>{t('nav.light')}</span>
										{preferredTheme === Theme.LIGHT ? <Check className="ml-auto size-4" /> : null}
									</CustomCommandItem>
									<CustomCommandItem onSelect={() => void handleSelectTheme(Theme.DARK)} value="Theme: Dark">
										<Moon className="mr-2 size-4" />
										<span>{t('nav.dark')}</span>
										{preferredTheme === Theme.DARK ? <Check className="ml-auto size-4" /> : null}
									</CustomCommandItem>
									<CustomCommandItem onSelect={() => void handleSelectTheme(Theme.SYSTEM)} value="Theme: System">
										<Monitor className="mr-2 size-4" />
										<span>{t('nav.system')}</span>
										{!preferredTheme ? <Check className="ml-auto size-4" /> : null}
									</CustomCommandItem>
								</CustomCommandGroup>
							</>
						) : null}
					</CommandList>
				</ScrollArea>
			</CommandDialog>
		</>
	);
};

type CommandTriggerProps = {
	readonly command?: string[];
	readonly handleOpenChange: () => void;
	readonly isMobile?: boolean;
	readonly isOpen: boolean;
	readonly showKeyboardNavigation?: boolean;
	readonly showTriggerText: boolean;
	readonly triggerClassName?: string;
};

const CommandTrigger = forwardRef<HTMLButtonElement, CommandTriggerProps>(
	(
		{
			command,
			handleOpenChange,
			isMobile = false,
			isOpen,
			showKeyboardNavigation,
			showTriggerText,
			triggerClassName,
			...props
		},
		forwardedRef,
	) => {
		const t = useTranslations();
		function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
			if (event.key === 'Enter') {
				handleOpenChange();
			}
		}

		return (
			<button
				aria-label={t('nav.search')}
				className={cn(
					'group/nav',
					isMobile
						? 'flex size-11 shrink-0 items-center justify-center'
						: 'flex h-6 w-full items-center justify-start gap-0 rounded-md border py-4 pr-2 pl-2 text-foreground transition-[gap,color,background-color,border-color,outline-color] delay-150 duration-300 group-focus-within:gap-3 group-focus-within:delay-0 group-hover:gap-3 group-hover:delay-0',
					!isMobile && !isOpen && 'group-focus-within:bg-muted group-hover:bg-muted',
					!isMobile && (!isOpen ? 'border-secondary hover:outline hover:outline-secondary' : 'border-border bg-muted'),
					triggerClassName,
					isMobile && isOpen && 'before:border-primary/60 before:bg-accent',
				)}
				onKeyDown={handleKeyDown}
				onMouseDown={handleOpenChange}
				ref={forwardedRef}
				title={t('nav.search')}
				type="button"
				{...props}
			>
				<Search className={cn('size-4 shrink-0 text-foreground', isMobile && 'relative z-10')} />
				{showTriggerText && !isOpen ? (
					<span
						className={cn(
							'max-w-0 overflow-hidden whitespace-nowrap text-left text-sm opacity-0 transition-[max-width,opacity] duration-300',
							'group-focus-within:max-w-[114px] group-focus-within:opacity-100',
							'group-hover:max-w-[114px] group-hover:opacity-100',
						)}
					>
						{t('nav.search')}
					</span>
				) : null}
				{showKeyboardNavigation ? <CommandBadge command={command} /> : null}
			</button>
		);
	},
);

CommandTrigger.displayName = 'NavigationCommandTrigger';

type CommandSearchResultsProps = {
	readonly data: InstrumentSearchQuoteResponseFiltered;
	readonly hasResults: boolean;
	readonly isIdle: boolean;
};

const CommandSearchResults = ({ data, hasResults, isIdle }: CommandSearchResultsProps) => {
	const t = useTranslations();
	const heading = !isIdle ? t('nav.searchResultsLoading') : hasResults ? t('nav.searchResults') : t('nav.noResults');

	if (!heading) {
		return null;
	}

	return (
		<CustomCommandGroup className="table w-full" customchildclassname="table w-full" heading={heading}>
			{!isIdle ? null : <CommandEmpty>{t('nav.noResultsFound')}</CommandEmpty>}
			{data.map((quote) => (
				<CustomCommandItem
					className="table-row w-full"
					href={`/instrument/${quote.symbol}`}
					key={quote.symbol}
					value={quote.symbol}
				>
					<span className="table-cell rounded-tl-sm rounded-bl-sm px-2 py-3 text-center align-middle text-sm">
						<BarChart2 className="size-4" />
					</span>
					<span className="table-cell px-2 py-3 text-left align-middle text-sm">{quote.shortname}</span>
					<span className="table-cell px-2 py-3 text-center align-middle text-sm">{quote.symbol}</span>
					<span className="table-cell px-2 py-3 text-center align-middle text-sm">{quote.quoteType}</span>
					<span className="table-cell rounded-tr-sm rounded-br-sm px-2 py-3 text-center align-middle text-sm">
						{quote.exchDisp}
					</span>
				</CustomCommandItem>
			))}
		</CustomCommandGroup>
	);
};

type CommandLatestSearchesProps = {
	readonly lastSearches:
		| {
				instrument_id: number | null;
				ticker: string | undefined;
		  }[]
		| null
		| undefined;
};

const CommandLatestSearches = ({ lastSearches }: CommandLatestSearchesProps) => {
	const t = useTranslations();
	return (
		<CommandGroup heading={t('nav.latestSearches')}>
			{lastSearches
				?.filter((suggestion) => suggestion.ticker)
				.map((suggestion) => (
					<CustomCommandItem
						className="flex w-full flex-row"
						href={`/instrument/${suggestion.ticker}`}
						key={suggestion.ticker}
						value={suggestion.ticker}
					>
						<BarChart2 className="mr-2 size-4" />
						<span>{suggestion.ticker}</span>
					</CustomCommandItem>
				))}
		</CommandGroup>
	);
};

type CommandSearchSuggestionsProps = {
	readonly searchSuggestions:
		| {
				instrument_id: number | null;
				ticker: string | undefined;
		  }[]
		| null
		| undefined;
};

const CommandSearchSuggestions = ({ searchSuggestions }: CommandSearchSuggestionsProps) => {
	const t = useTranslations();
	if (!searchSuggestions) {
		return null;
	}

	return (
		<CommandGroup heading={t('nav.popularSearches')}>
			{searchSuggestions
				.filter((suggestion) => suggestion.ticker)
				.map((suggestion) => (
					<CustomCommandItem
						className="flex w-full flex-row"
						href={`/instrument/${suggestion.ticker}`}
						key={suggestion.ticker}
						value={suggestion.ticker}
					>
						<BarChart2 className="mr-2 size-4" />
						<span>{suggestion.ticker}</span>
					</CustomCommandItem>
				))}
		</CommandGroup>
	);
};

type CommandNavigationOptionProps = {
	readonly command: string[];
	readonly href: string;
	readonly Icon: React.ReactElement;
	readonly name: string;
	readonly unreadCountLabel?: string;
};

const CommandNavigationOption = ({ command, href, Icon, name, unreadCountLabel }: CommandNavigationOptionProps) => {
	const t = useTranslations();
	const navigate = useNavigate();
	const notificationLabel =
		href === '/notifications'
			? unreadCountLabel
				? t('nav.unreadNotifications', { count: unreadCountLabel })
				: name
			: undefined;

	async function handleNavigate(): Promise<void> {
		if (typeof window === 'undefined') {
			await navigate({ to: href });
			return;
		}

		const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;

		await navigate({ to: href });

		window.setTimeout(() => {
			const nextLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;

			if (nextLocation === currentLocation) {
				window.location.assign(href);
			}
		}, 150);
	}

	useKeyboardShortcut({
		callback: () => {
			void handleNavigate();
		},
		command,
		shouldFire: true,
	});

	return (
		<CustomCommandItem
			aria-label={notificationLabel}
			className={unreadCountLabel ? 'text-primary hover:text-primary aria-selected:text-primary' : undefined}
			href={href}
			onSelect={() => void handleNavigate()}
			title={notificationLabel}
			value={name}
		>
			{Icon}
			<span>{name}</span>
			{unreadCountLabel ? (
				<span aria-hidden="true" className="ml-auto shrink-0 tabular-nums">
					{unreadCountLabel}
				</span>
			) : null}
			<CommandShortcut className="hidden sm:block">
				<CommandBadge className="relative mr-12 p-1 px-2" command={command} />
			</CommandShortcut>
		</CustomCommandItem>
	);
};
